import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const VERSAO = '1.2';
const STORAGE_PREFIX = `popup_novidades_v${VERSAO}_`;

interface Novidade {
  icon: React.ReactNode;
  titulo: string;
  descricao: string;
}

const NOVIDADES: Novidade[] = [
  {
    icon: (
      <path
        d="M3 4h18v12H3zM8 20h8M12 16v4"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    titulo: 'Nova tela de login',
    descricao: 'Layout dividido com vitrine de recursos do sistema: agenda de cargas, agenda de visitas, fluxo de etapas, comprovante e indicadores passando em slides.'
  },
  {
    icon: (
      <>
        <rect x="4" y="10" width="16" height="11" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 10V7a4 4 0 118 0v3" stroke="currentColor" strokeWidth="1.8" />
      </>
    ),
    titulo: 'Senhas mais seguras',
    descricao: 'Senhas agora exigem mínimo de 8 caracteres com checklist visual de requisitos (maiúscula, minúscula, número e caractere especial).'
  },
  {
    icon: (
      <>
        <circle cx="12" cy="9" r="4" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4 21c0-4 3.6-7 8-7s8 3 8 7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    ),
    titulo: 'Selfie reutilizada',
    descricao: 'Ao agendar uma nova visita, a foto do documento da última visita é reaproveitada automaticamente. Sem precisar anexar de novo.'
  },
  {
    icon: (
      <>
        <path d="M3 5h18M3 11h18M3 17h12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    ),
    titulo: 'Minhas Solicitações sem rolagem',
    descricao: 'A tabela agora cabe na tela sem scroll lateral, com ação no final fixa e a solicitação mais recente destacada no topo.'
  },
  {
    icon: (
      <>
        <rect x="3" y="4" width="18" height="17" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 9h18M8 4v5" stroke="currentColor" strokeWidth="1.8" />
      </>
    ),
    titulo: 'Agenda por comprador',
    descricao: 'Na Gestão de Visitas, cada comprador vê apenas os horários e visitas da própria agenda. Menos poluição visual.'
  },
  {
    icon: (
      <path
        d="M12 2L4 6v6c0 5 3.4 8.6 8 10 4.6-1.4 8-5 8-10V6l-8-4z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    ),
    titulo: 'Comunicação reforçada',
    descricao: 'Ajustes de segurança no backend e na conversa entre frontend e API.'
  }
];

const NovidadesPopup: React.FC = () => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    const dismissed = localStorage.getItem(`${STORAGE_PREFIX}${user.id}`);
    if (!dismissed) {
      const timer = setTimeout(() => setIsOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleClose = () => {
    setIsOpen(false);
    if (user) {
      localStorage.setItem(`${STORAGE_PREFIX}${user.id}`, 'dismissed');
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,.6)',
        backdropFilter: 'blur(4px)',
        padding: '28px',
        animation: 'fadeIn .25s ease',
      }}
      onClick={handleClose}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes popupIn { from { opacity: 0; transform: scale(.96) } to { opacity: 1; transform: scale(1) } }
      `}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="novidadesTitle"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '560px',
          maxHeight: '92vh',
          background: '#0E1930',
          border: '1px solid #233A5C',
          borderRadius: '20px',
          boxShadow: '0 40px 100px rgba(0,0,0,.6)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          animation: 'popupIn .3s ease',
        }}
      >
        {/* Close button */}
        <button
          aria-label="Fechar"
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '18px',
            right: '18px',
            zIndex: 5,
            width: '34px',
            height: '34px',
            borderRadius: '10px',
            border: '1px solid rgba(255,255,255,.12)',
            background: 'rgba(8,14,26,.5)',
            color: '#AEB9CB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: '.15s ease',
          }}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        {/* Hero */}
        <div
          style={{
            position: 'relative',
            padding: '28px 32px 24px',
            background:
              'radial-gradient(600px 260px at 85% -30%, rgba(143,203,78,.14), transparent 60%), linear-gradient(160deg, #10203E 0%, #0A1730 100%)',
            overflow: 'hidden',
            borderBottom: '1px solid #233A5C',
          }}
        >
          <img
            src="/Falcon Hub 2.png"
            alt="Falcon Hub"
            style={{
              display: 'block',
              height: '32px',
              width: 'auto',
              marginBottom: '18px',
              filter: 'drop-shadow(0 2px 6px rgba(0,0,0,.4))',
            }}
          />

          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '7px',
              background: 'linear-gradient(135deg, #E5AC40, #C88A26)',
              color: '#241701',
              fontSize: '11px',
              fontWeight: 800,
              letterSpacing: '.09em',
              padding: '5px 11px 5px 9px',
              borderRadius: '20px',
              marginBottom: '14px',
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
              <path
                d="M12 2l2.6 6.6L22 9l-5.4 4.6L18 21l-6-3.8L6 21l1.4-7.4L2 9l7.4-.4L12 2z"
                fill="#241701"
              />
            </svg>
            NOVA VERSÃO · v{VERSAO}
          </div>

          <h1
            id="novidadesTitle"
            style={{
              margin: '0 0 8px',
              fontSize: 'clamp(22px, 4vw, 27px)',
              lineHeight: 1.15,
              fontWeight: 900,
              letterSpacing: '-.01em',
              color: '#F3F6FA',
            }}
          >
            O que mudou na{' '}
            <span style={{ color: '#8FCB4E', textShadow: '0 0 24px rgba(143,203,78,.35)' }}>v{VERSAO}</span>
          </h1>

          <p
            style={{
              margin: 0,
              fontSize: '13.5px',
              lineHeight: 1.55,
              color: '#AEB9CB',
            }}
          >
            Login repaginado, segurança reforçada e ajustes que deixam o dia a dia mais rápido.
          </p>
        </div>

        {/* Lista de novidades */}
        <div style={{ overflowY: 'auto', padding: '18px 32px 12px' }}>
          {NOVIDADES.map((n) => (
            <div
              key={n.titulo}
              style={{
                display: 'flex',
                gap: '14px',
                alignItems: 'flex-start',
                padding: '12px 0',
                borderBottom: '1px solid rgba(35,58,92,.5)',
              }}
            >
              <div
                style={{
                  flex: '0 0 auto',
                  width: '34px',
                  height: '34px',
                  borderRadius: '9px',
                  background: 'rgba(143,203,78,.12)',
                  border: '1px solid rgba(143,203,78,.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" style={{ width: '17px', height: '17px', color: '#8FCB4E' }}>
                  {n.icon}
                </svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#F3F6FA', marginBottom: '3px' }}>
                  {n.titulo}
                </div>
                <div style={{ fontSize: '12.5px', lineHeight: 1.5, color: '#AEB9CB' }}>
                  {n.descricao}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 32px 22px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '14px',
            borderTop: '1px solid #233A5C',
            background: '#0E1930',
          }}
        >
          <button
            onClick={handleClose}
            style={{
              fontFamily: 'inherit',
              fontSize: '14px',
              fontWeight: 800,
              letterSpacing: '.01em',
              padding: '12px 26px',
              borderRadius: '11px',
              cursor: 'pointer',
              border: 'none',
              background: 'linear-gradient(135deg, #8FCB4E, #6FA637)',
              color: '#0C1704',
              boxShadow: '0 10px 24px rgba(143,203,78,.32)',
              transition: '.15s ease',
            }}
          >
            Entendi, vamos lá
          </button>
        </div>
      </div>
    </div>
  );
};

export default NovidadesPopup;
